import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Divider, Grid, Stack, Chip, Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import CategoryIcon from '@mui/icons-material/Category';

const StatItem = ({ label, value, hint }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography variant="h6">{value}</Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
      )}
    </Stack>
  </Stack>
);

const SectionTitle = ({ icon, text }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
    {icon}
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{text}</Typography>
  </Stack>
);

const MemberCheckedOutContent = ({ dialogDatas, onClose }) => {
  const user = dialogDatas?.user ?? {};
  const event = dialogDatas?.event ?? {};
  const masterTags = dialogDatas?.masterTags ?? [];
  const rawCategoryStats = dialogDatas?.categoryStats
    ?? dialogDatas?.checkout?.category_stats
    ?? [];
  const checkout = dialogDatas?.checkout ?? null;
  const eventUsers = Array.isArray(dialogDatas?.eventUsers) ? dialogDatas.eventUsers : [];
  const isAborted = checkout?.completion_status === 'aborted';
  const [showNormalResult, setShowNormalResult] = useState(false);
  const shouldShowAbortedResult = isAborted && !showNormalResult;
  const abortedResult = checkout?.aborted_result ?? {};

  const earnedRoomPoints = checkout?.remaining_room_points ?? user?.point ?? 0;
  const initialRoomPoints = Number(event?.entry_room_points ?? 0);
  const earnedRoomPointDelta = earnedRoomPoints - initialRoomPoints;

  // 個人成績（UI側で派生）
  const wins = user?.wins ?? 0;
  const played = user?.rounds_played ?? 0;
  const losses = Math.max(0, played - wins);
  const winRate = played > 0 ? Math.round((wins / played) * 100) : 0;
  const maxWinStreak = Number(checkout?.my_result?.max_win_streak ?? user?.max_win_streak);

  // 全体情報（events / 派生）
  const participants = event?.participants_count ?? 0;
  const totalBet = event?.total_bet ?? 0;
  const roundsCount = event?.round ?? 0; // ※運用次第では rounds 件数に差し替え
  const startedAt = event?.created_at ?? event?.createdAt ?? '';

  // 見た目用のフォーマッタ
  const fmt = useMemo(() => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }), []);
  const fmtDate = (s) => {
    if (!s) return '';
    try {
      return new Date(s).toLocaleString();
    } catch {
      return String(s);
    }
  };

  const categoryNameMap = useMemo(() => {
    return new Map(masterTags.map((tag) => [Number(tag.id), tag.name]));
  }, [masterTags]);

  const categoryStats = useMemo(() => {
    return rawCategoryStats
      .map((stat) => ({
        ...stat,
        categoryName: categoryNameMap.get(Number(stat.category_id)) ?? `カテゴリ${stat.category_id}`,
      }))
      .sort((a, b) => a.categoryName.localeCompare(b.categoryName, 'ja'));
  }, [rawCategoryStats, categoryNameMap]);

  const formatPointWithSign = (value) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${fmt.format(value)} pt`;
  };

  const formatPercent = (value) => `${fmt.format(Number(value ?? 0) * 100)} %`;

  const formatPercentWithSign = (value) => {
    const numeric = Number(value ?? 0) * 100;
    const sign = numeric > 0 ? '+' : '';
    return `${sign}${fmt.format(numeric)} %`;
  };

  const hasCategoryStats = categoryStats.length > 0;

  useEffect(() => {
    setShowNormalResult(false);
  }, [checkout?.completion_status, checkout?.ended_at]);

  const rivalComparison = useMemo(() => {
    const userId = user?.id ?? user?.user_id;
    const myRecord = eventUsers.find((u) => u.user_id === userId);

    const rawRivals = user?.rivals;
    let rivalIds = [];
    if (Array.isArray(rawRivals)) {
      rivalIds = rawRivals.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    } else if (typeof rawRivals === 'string') {
      try {
        const parsed = JSON.parse(rawRivals || '[]');
        if (Array.isArray(parsed)) {
          rivalIds = parsed.map((id) => Number(id)).filter((id) => Number.isFinite(id));
        }
      } catch {
        rivalIds = [];
      }
    }

    const rivals = eventUsers.filter((u) => rivalIds.includes(Number(u.user_id)));
    if (!myRecord || rivals.length === 0) return [];

    const myWinRate = (myRecord.rounds_played ?? 0) > 0
      ? (myRecord.wins ?? 0) / myRecord.rounds_played
      : 0;

    return rivals.map((rival) => {
      const rivalWinRate = (rival.rounds_played ?? 0) > 0
        ? (rival.wins ?? 0) / rival.rounds_played
        : 0;
      const isWin = myWinRate >= rivalWinRate;
      return {
        rival,
        myWinRate,
        rivalWinRate,
        resultLabel: isWin ? '勝ち' : '負け',
        color: isWin ? 'success' : 'error',
      };
    });
  }, [eventUsers, user]);

  if (shouldShowAbortedResult) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2 }, minWidth: { xs: 320, sm: 420 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <CheckCircleIcon color="warning" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            このルームは途中で終了しました
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          オーナーの操作が一定時間行われなかったため、ルームを終了しました。
        </Typography>

        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              最終ラウンドについて
            </Typography>
            <Typography variant="body2" color="text.secondary">
              結果が確定しなかったため、このラウンドは集計の対象外です。
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {abortedResult?.bet_refunded
                ? `ベットした${fmt.format(abortedResult.refunded_bet_points ?? 0)}ptは返却されています。`
                : '返却対象のベットはありません。'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              参加チケットについて
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ルームが予定どおり完了しなかったため、参加に使用した参加チケットを返却しました。
            </Typography>
          </Box>
        </Stack>

        {checkout?.ended_at && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            終了日時: {fmtDate(checkout.ended_at)}
          </Typography>
        )}
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="contained" onClick={() => setShowNormalResult(true)}>
            ルームの最終戦績を確認する
          </Button>
        </Stack>
      </Box>
    );
  }


  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, minWidth: { xs: 320, sm: 420 } }}>
        {/* ヘッダ */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            ルームの最終結果
          </Typography>
        </Stack>

        {/* 獲得ルームチップ */}
        <SectionTitle
          icon={<TrendingUpIcon fontSize="small" color="primary" />}
          text="獲得ルームチップ"
        />
        <Box
          sx={{
            p: 1, borderRadius: 2, bgcolor: 'background.default',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            mb: 2
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <StatItem
                label="獲得ルームチップ"
                value={`${fmt.format(earnedRoomPoints)}pt (${formatPointWithSign(earnedRoomPointDelta)})`}
              />
            </Grid>
          </Grid>
        </Box>

        {hasCategoryStats && (
          <>
            <SectionTitle
              icon={<CategoryIcon fontSize="small" color="secondary" />}
              text="カテゴリ別収支の変化"
            />
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {categoryStats.map((stat) => {
                const pointDelta = Number(stat.point_delta ?? 0);
                const deltaColor = pointDelta >= 0 ? 'success' : 'default';

                return (
                  <Box
                    key={stat.category_id}
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: 'background.default',
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {stat.categoryName}
                      </Typography>
                      <Chip
                        size="small"
                        label={`増減 ${formatPointWithSign(pointDelta)}`}
                        color={deltaColor}
                        variant={pointDelta >= 0 ? 'filled' : 'outlined'}
                      />
                    </Stack>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <StatItem label="精算前" value={`${fmt.format(stat.point_before ?? 0)} pt`} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <StatItem label="精算後" value={`${fmt.format(stat.point_after ?? 0)} pt`} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <StatItem label="ポイント増減" value={formatPointWithSign(pointDelta)} />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} sm={4}>
                        <StatItem label="勝率（前）" value={formatPercent(stat.win_rate_before)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <StatItem label="勝率（後）" value={formatPercent(stat.win_rate_after)} />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <StatItem label="勝率増減" value={formatPercentWithSign(stat.win_rate_delta)} />
                      </Grid>
                    </Grid>
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`勝ち数 ${stat.wins_before} → ${stat.wins_after}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`勝ち増減 ${stat.wins_delta >= 0 ? '+' : ''}${stat.wins_delta}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`参加ラウンド ${stat.rounds_before} → ${stat.rounds_after}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`ラウンド増減 ${stat.rounds_delta >= 0 ? '+' : ''}${stat.rounds_delta}`}
                      />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </>
        )}

        {/* 自分の戦績 */}
        <SectionTitle
          icon={<EmojiEventsIcon fontSize="small" color="warning" />}
          text="ルーム内での自分の戦績"
        />
        <Box
          sx={{
            p: 1, borderRadius: 2, bgcolor: 'background.default',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            mb: 2
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <StatItem label="参加ラウンド数" value={`${fmt.format(played)} 回`} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatItem label="勝ち数" value={`${fmt.format(wins)} 回`} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatItem label="負け数" value={`${fmt.format(losses)} 回`} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatItem label="勝率" value={`${fmt.format(winRate)} %`} />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {typeof user?.rank === 'number' && (
              <Chip size="small" color="primary" icon={<LeaderboardIcon />} label={`最終順位: ${user.rank} 位`} />
            )}
            {Number.isFinite(maxWinStreak) && maxWinStreak >= 2 && (
              <Chip size="small" label={`最高連続的中: ${maxWinStreak}`} />
            )}
            {typeof user?.balance === 'number' && (
              <Chip
                size="small"
                label={`収支: ${user.balance >= 0 ? '+' : ''}${fmt.format(user.balance)} pt`}
                variant={user.balance >= 0 ? 'filled' : 'outlined'}
                color={user.balance >= 0 ? 'success' : 'default'}
              />
            )}
          </Stack>
        </Box>


        {rivalComparison.length > 0 && (
          <>
            <SectionTitle
              icon={<LeaderboardIcon fontSize="small" color="info" />}
              text="ライバルとの成績比較"
            />
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              {rivalComparison.map(({ rival, myWinRate, rivalWinRate, resultLabel, color }) => (
                <Box
                  key={rival.user_id}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {rival.name || rival.nickname || `ユーザーID: ${rival.user_id}`}
                    </Typography>
                    <Chip size="small" color={color} label={resultLabel} />
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <StatItem label="あなたの勝率" value={formatPercent(myWinRate)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <StatItem label="ライバルの勝率" value={formatPercent(rivalWinRate)} />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Stack>
          </>
        )}

        {/* 参加者全体の情報 */}
        <SectionTitle
          icon={<LeaderboardIcon fontSize="small" color="secondary" />}
          text="参加者全体の情報"
        />
        <Box
          sx={{
            p: 1, borderRadius: 2, bgcolor: 'background.default',
            border: (theme) => `1px solid ${theme.palette.divider}`
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <StatItem label="参加者数" value={`${fmt.format(participants)} 人`} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatItem label="ルーム内総ベット額" value={`${fmt.format(totalBet)} pt`} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatItem label="ラウンド数" value={`${fmt.format(roundsCount)} 回`} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <StatItem label="ルーム作成" value={fmtDate(startedAt)} />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          {/* <Tooltip title="結果をコピー（将来拡張の余地）">
            <span>
              <Button size="small" disabled>コピー</Button>
            </span>
          </Tooltip> */}
          {onClose && (
            <Button variant="contained" onClick={onClose}>閉じる</Button>
          )}
        </Stack>
    </Box>
  );
};

export default MemberCheckedOutContent;
